import { formatClock, useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import type { AuditEntry } from '../../pages/admin/auditLogStore'

// "openDate" or "team_id" as "Open date" or "Team id".
function label(key: string): string {
  const words = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// A clock time as the database holds it, "22:00:00", follows the time format
// the reader chose; every other value is shown exactly as stored.
const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

function show(value: unknown, format: TimeFormat): string {
  if (value === null || value === undefined || value === '') return 'None'
  if (typeof value === 'string' && CLOCK_TIME.test(value)) return formatClock(value, format)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// A labeled group rather than a heading: the log page has one h1, and a
// heading here would skip a level.
function RecordView({ id, heading, value }: { id: string; heading: string; value: Record<string, unknown> | null }) {
  const { format } = useTimeFormat()
  return (
    <div role="group" aria-labelledby={id}>
      <p id={id} className="text-caption font-bold tracking-wide text-ink-subtle uppercase">
        {heading}
      </p>
      {value === null ? (
        <p className="mt-1 text-small text-ink-muted">Nothing recorded.</p>
      ) : (
        <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-small">
          {Object.entries(value).map(([key, entry]) => (
            <div key={key} className="contents">
              <dt className="text-ink-muted">{label(key)}</dt>
              <dd className="font-medium break-all">{show(entry, format)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

/**
 * One event's record as the audit log holds it: the record before the change
 * and after it, and the reason when there is one. Shown exactly as stored, so
 * the log never says more than it recorded.
 */
export default function AuditEventDetails({ entry }: { entry: AuditEntry }) {
  return (
    <div className="flex flex-col gap-4">
      {entry.reason && (
        <p className="text-small">
          <span className="font-bold">Reason:</span> {entry.reason}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RecordView id={`audit-before-${entry.id}`} heading="Before" value={entry.previous} />
        <RecordView id={`audit-after-${entry.id}`} heading="After" value={entry.next} />
      </div>
    </div>
  )
}
